/**
 * Cockpit charts (decision 112) — hand-rolled SVG, no chart library.
 *
 * Follows the dataviz ground rules: ONE axis per chart (never dual-axis — the
 * count and £ views are separate aligned panels), thin bars with rounded
 * data-ends anchored to the baseline, a 2px surface gap between adjacent bars,
 * recessive grid, text in ink tokens (never the series colour), a legend for
 * two series, hover tooltips on every mark, and a table fallback per chart.
 * Palette: Wave9 #006699 / #0099CC — validated (CVD ΔE 15.3, contrast ≥3:1).
 */
import { useState, type ReactNode } from 'react'
import { formatGBP } from '@shared/money'

/**
 * Series colours are CSS variables so a section can re-palette its charts:
 * decision 201's "New business only" paints the activity section GREEN
 * (dark green for the emphasis series, a lighter green for the context
 * series — see `.activity--nb` in theme.css), so the mode is visible from
 * across the room without reading a subtitle. Fallbacks are the brand blues.
 */
export const CHART_BLUE = 'var(--chart-blue, #006699)'      // accepted / the emphasis series
export const CHART_LIGHT = 'var(--chart-light, #0099CC)'    // sent / the context series
/** Decision 201: the third hue for One-off · ARR · TCV — a cool grey-blue that
 * separates from both blues for CVD readers and stays in the brand family. */
export const CHART_GREY = 'var(--chart-grey, #8FA9B8)'
export const SPLIT_COLOURS: [string, string, string] = [CHART_GREY, CHART_LIGHT, CHART_BLUE]   // one-off · ARR · TCV
/** Decision 201: the acceptance-rate line on the value chart — a warm accent so it never reads as a fourth money series. */
export const RATE_COLOUR = '#C4711A'

/** Decision 201: axis-gutter £ — £850 · £42k · £1.2M. Tooltips keep the full figure. */
export const gbpAxis = (v: number): string =>
  v >= 1_000_000 ? `£${(v / 1_000_000).toFixed(v % 1_000_000 === 0 ? 0 : 1)}M`
  : v >= 1000 ? `£${Math.round(v / 1000)}k`
  : `£${Math.round(v)}`

/** Drawing box. `wide` is for full-width panels (decision 201 layout): a
 * 1000×200 box scales to the page width without ballooning in height. */
interface Dims { W: number; H: number }
const NARROW: Dims = { W: 460, H: 170 }
const WIDE: Dims = { W: 1000, H: 200 }
const PAD = { top: 12, right: 8, bottom: 22, left: 34 }

const monthShort = (ym: string) => {
  const [y, m] = ym.split('-').map(Number)
  const label = new Date(Date.UTC(y, m - 1, 1)).toLocaleString('en-GB', { month: 'short', timeZone: 'UTC' })
  return m === 1 ? `${label} ${String(y).slice(2)}` : label
}

function niceMax(v: number): number {
  if (v <= 4) return Math.max(2, v)
  const mag = 10 ** Math.floor(Math.log10(v))
  for (const m of [1, 2, 2.5, 5, 10]) if (v <= m * mag) return m * mag
  return 10 * mag
}

interface Tip { x: number; y: number; body: ReactNode }

/** Tooltip positioned as a PERCENTAGE of the plot box, so it tracks the
 * responsive SVG at any rendered size. Wrap the svg + node in one
 * position:relative div containing nothing else. */
function useTip(D: Dims) {
  const [tip, setTip] = useState<Tip | null>(null)
  const node = tip && (
    <div style={{
      position: 'absolute', left: `${(tip.x / D.W) * 100}%`, top: `${(tip.y / D.H) * 100}%`,
      transform: 'translate(-50%, -115%)',
      background: '#fff', border: '1px solid var(--line)', borderRadius: 8, padding: '6px 10px',
      fontSize: 12, boxShadow: '0 4px 14px rgba(10,40,60,0.13)', pointerEvents: 'none', zIndex: 5,
      whiteSpace: 'nowrap',
    }}>{tip.body}</div>
  )
  return { setTip, node }
}

function Frame({ D, yMax, yFmt, ticks: tickValues, children }: { D: Dims; yMax: number; yFmt: (v: number) => string; ticks?: number[]; children: ReactNode }) {
  const ticks = tickValues ?? [0, 0.5, 1].map((f) => f * yMax)
  const y = (v: number) => PAD.top + (D.H - PAD.top - PAD.bottom) * (1 - v / yMax)
  return (
    <svg viewBox={`0 0 ${D.W} ${D.H}`} style={{ width: '100%', height: 'auto', display: 'block' }} role="img">
      {ticks.map((t) => (
        <g key={t}>
          <line x1={PAD.left} x2={D.W - PAD.right} y1={y(t)} y2={y(t)} stroke="var(--line-soft, #e8eef2)" strokeWidth={1} />
          <text x={PAD.left - 5} y={y(t) + 3.5} textAnchor="end" fontSize={9.5} fill="var(--ink-soft, #66757e)">{yFmt(t)}</text>
        </g>
      ))}
      {children}
    </svg>
  )
}

const plotX = (D: Dims, i: number, n: number) => {
  const span = D.W - PAD.left - PAD.right
  return PAD.left + (span / n) * i
}
const plotY = (D: Dims, v: number, yMax: number) => PAD.top + (D.H - PAD.top - PAD.bottom) * (1 - v / yMax)

/** Rounded-top bar anchored to the baseline (4px radius data-end). */
function Bar({ D, x, w, v, from = 0, flat = false, yMax, fill, onEnter, onLeave }: {
  D: Dims; x: number; w: number; v: number; from?: number; yMax: number; fill: string
  /** A segment with another stacked on top: square-topped, so the joins are seamless. */
  flat?: boolean
  onEnter: (cx: number, cy: number) => void; onLeave: () => void
}) {
  const y0 = plotY(D, from, yMax)
  const y1 = plotY(D, from + v, yMax)
  const h = Math.max(0, y0 - y1)
  const r = flat ? 0 : Math.min(4, w / 2, h)
  const d = h === 0
    ? ''
    : `M ${x} ${y0} V ${y1 + r} Q ${x} ${y1} ${x + r} ${y1} H ${x + w - r} Q ${x + w} ${y1} ${x + w} ${y1 + r} V ${y0} Z`
  return (
    <g onMouseEnter={() => onEnter(x + w / 2, y1)} onMouseLeave={onLeave} style={{ cursor: 'default' }}>
      {/* hit target bigger than the mark */}
      <rect x={x - 2} y={PAD.top} width={w + 4} height={D.H - PAD.top - PAD.bottom} fill="transparent" />
      {h > 0 && <path d={d} fill={fill} />}
    </g>
  )
}

function XLabels({ D, labels }: { D: Dims; labels: string[] }) {
  return (
    <>
      {labels.map((l, i) => (
        <text key={i} x={plotX(D, i, labels.length) + (D.W - PAD.left - PAD.right) / labels.length / 2}
          y={D.H - 8} textAnchor="middle" fontSize={9} fill="var(--ink-soft, #66757e)">{l}</text>
      ))}
    </>
  )
}

function TableFallback({ head, rows }: { head: string[]; rows: Array<Array<string | number>> }) {
  return (
    <details style={{ marginTop: 4 }}>
      <summary className="tiny muted" style={{ cursor: 'pointer' }}>View as table</summary>
      <table className="small" style={{ marginTop: 4 }}>
        <thead><tr>{head.map((h) => <th key={h} style={{ textAlign: 'left', paddingRight: 10 }}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((r, i) => <tr key={i}>{r.map((c, j) => <td key={j} style={{ paddingRight: 10 }}>{c}</td>)}</tr>)}
        </tbody>
      </table>
    </details>
  )
}

/** Two-series grouped bars (counts) — legend + hover tooltip. */
export function GroupedBarChart({ data, names, format, axisFormat, fyMonth, wide }: {
  data: Array<{ label: string; month: string; a: number; b: number }>
  names: [string, string]     // [a, b] — a wears CHART_LIGHT, b wears CHART_BLUE
  format?: (v: number) => string
  /** Decision 201: a shorter formatter for the axis gutter (defaults to `format`). */
  axisFormat?: (v: number) => string
  /** Decision 201: draw the financial-year divider before this month (YYYY-MM). */
  fyMonth?: string
  wide?: boolean
}) {
  const D = wide ? WIDE : NARROW
  const { setTip, node } = useTip(D)
  const fmt = format ?? ((v: number) => String(v))
  const axisFmt = axisFormat ?? fmt
  const yMax = niceMax(Math.max(1, ...data.map((d) => Math.max(d.a, d.b))))
  const n = data.length
  const slot = (D.W - PAD.left - PAD.right) / n
  const barW = Math.max(4, (slot - 8) / 2 - 1)   // 2px gap between the pair
  return (
    <div>
      <div className="rowflex" style={{ gap: 12, marginBottom: 2 }}>
        <span className="tiny" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <span style={{ width: 9, height: 9, borderRadius: 3, background: CHART_LIGHT, display: 'inline-block' }} />{names[0]}
        </span>
        <span className="tiny" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <span style={{ width: 9, height: 9, borderRadius: 3, background: CHART_BLUE, display: 'inline-block' }} />{names[1]}
        </span>
      </div>
      <div style={{ position: 'relative' }}>
        <Frame D={D} yMax={yMax} yFmt={axisFmt}>
          {data.map((d, i) => {
            const x0 = plotX(D, i, n) + (slot - barW * 2 - 2) / 2
            const enter = (which: 0 | 1) => (cx: number, cy: number) => setTip({
              x: cx, y: cy,
              body: <><strong>{d.label}</strong> — {names[which]}: {fmt(which === 0 ? d.a : d.b)}</>,
            })
            return (
              <g key={d.month}>
                {fyMonth && d.month === fyMonth && i > 0 && (
                  <line x1={plotX(D, i, n)} x2={plotX(D, i, n)} y1={PAD.top} y2={D.H - PAD.bottom}
                    stroke="var(--ink-soft, #66757e)" strokeWidth={1} strokeDasharray="3 3" opacity={0.6}>
                    <title>Financial year starts</title>
                  </line>
                )}
                <Bar D={D} x={x0} w={barW} v={d.a} yMax={yMax} fill={CHART_LIGHT} onEnter={enter(0)} onLeave={() => setTip(null)} />
                <Bar D={D} x={x0 + barW + 2} w={barW} v={d.b} yMax={yMax} fill={CHART_BLUE} onEnter={enter(1)} onLeave={() => setTip(null)} />
              </g>
            )
          })}
          <XLabels D={D} labels={data.map((d) => d.label)} />
        </Frame>
        {node}
      </div>
      <TableFallback head={['Month', names[0], names[1]]} rows={data.map((d) => [d.label, fmt(d.a), fmt(d.b)])} />
    </div>
  )
}

/** Single-series bars (one hue — magnitude). */
export function BarChart({ data, format, name, axisFormat, wide }: {
  data: Array<{ label: string; month: string; v: number }>
  format: (v: number) => string
  name: string
  /** A shorter formatter for the axis gutter (defaults to `format`). */
  axisFormat?: (v: number) => string
  /** Decision 254: the board pack's 13-month history runs full width. */
  wide?: boolean
}) {
  const D = wide ? WIDE : NARROW
  const { setTip, node } = useTip(D)
  const yMax = niceMax(Math.max(1, ...data.map((d) => d.v)))
  const n = data.length
  const slot = (D.W - PAD.left - PAD.right) / n
  const barW = Math.max(6, slot - 8)
  return (
    <div>
      <div style={{ position: 'relative' }}>
        <Frame D={D} yMax={yMax} yFmt={axisFormat ?? format}>
          {data.map((d, i) => (
            <Bar D={D} key={d.month} x={plotX(D, i, n) + (slot - barW) / 2} w={barW} v={d.v} yMax={yMax} fill={CHART_BLUE}
              onEnter={(cx, cy) => setTip({ x: cx, y: cy, body: <><strong>{d.label}</strong> — {name}: {format(d.v)}</> })}
              onLeave={() => setTip(null)} />
          ))}
          <XLabels D={D} labels={data.map((d) => d.label)} />
        </Frame>
        {node}
      </div>
      <TableFallback head={['Month', name]} rows={data.map((d) => [d.label, format(d.v)])} />
    </div>
  )
}

/**
 * Decision 255: an OVERLAY bar chart — the inner series drawn as a narrower
 * bar on top of the outer one, same £ axis (one axis; nothing is stacked —
 * ARR is a part-of-the-picture figure that is always below the TCV bar it
 * sits in). Legend for the pair, hover tooltip carries both, table fallback.
 */
export function OverlayBarChart({ data, names, format, axisFormat, wide }: {
  data: Array<{ label: string; month: string; outer: number; inner: number; inner2?: number; muted?: boolean }>
  /** [outer, inner, inner2?] — outer wears CHART_BLUE; inner CHART_LIGHT and inner2 (decision 256: setup) CHART_GREY are STACKED inside it (setup at the base, the recurring above — together they are the first year). */
  names: [string, string] | [string, string, string]
  format: (v: number) => string
  axisFormat?: (v: number) => string
  wide?: boolean
}) {
  const D = wide ? WIDE : NARROW
  const { setTip, node } = useTip(D)
  const yMax = niceMax(Math.max(1, ...data.map((d) => Math.max(d.outer, d.inner + (d.inner2 ?? 0)))))
  const n = data.length
  const slot = (D.W - PAD.left - PAD.right) / n
  const barW = Math.max(6, slot - 8)
  const innerW = Math.max(3, barW * 0.5)
  const colours = [CHART_BLUE, CHART_LIGHT, CHART_GREY]
  const has2 = names.length === 3
  return (
    <div>
      <div className="rowflex" style={{ gap: 12, marginBottom: 2 }}>
        {names.map((nm, i) => (
          <span key={nm} className="tiny" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <span style={{ width: 9, height: 9, borderRadius: 3, background: colours[i], display: 'inline-block' }} />{nm}
          </span>
        ))}
      </div>
      <div style={{ position: 'relative' }}>
        <Frame D={D} yMax={yMax} yFmt={axisFormat ?? format}>
          {data.map((d, i) => {
            const x = plotX(D, i, n) + (slot - barW) / 2
            const tip = (cx: number, cy: number) => setTip({ x: cx, y: cy, body: <><strong>{d.label}</strong> — {names[0]}: {format(d.outer)} · {names[1]}: {format(d.inner)}{has2 ? <> · {names[2]}: {format(d.inner2 ?? 0)}</> : null}{d.muted ? ' · hand-entered' : ''}</> })
            return (
              <g key={d.month}>
                <Bar D={D} x={x} w={barW} v={d.outer} yMax={yMax} fill={CHART_BLUE} onEnter={tip} onLeave={() => setTip(null)} />
                {has2 && <Bar D={D} x={x + (barW - innerW) / 2} w={innerW} v={d.inner2 ?? 0} yMax={yMax} fill={CHART_GREY} onEnter={tip} onLeave={() => setTip(null)} />}
                <Bar D={D} x={x + (barW - innerW) / 2} w={innerW} v={d.inner} from={has2 ? d.inner2 ?? 0 : 0} yMax={yMax} fill={CHART_LIGHT} onEnter={tip} onLeave={() => setTip(null)} />
              </g>
            )
          })}
          <XLabels D={D} labels={data.map((d) => d.label)} />
        </Frame>
        {node}
      </div>
      <TableFallback head={['Month', ...names]} rows={data.map((d) => [d.label, format(d.outer), format(d.inner), ...(has2 ? [format(d.inner2 ?? 0)] : [])])} />
    </div>
  )
}

/**
 * Decision 256 (Mark: "please stack"): a STACKED bar whose parts sum to the
 * whole — one-off at the base, ARR (year 1) above, later years on top, so
 * the bar's top is the TCV. Parts must be real, additive money; the caller
 * derives the top part as the remainder. Legend, tooltip with every part
 * and the total, table fallback.
 */
export function StackedBarChart({ data, names, format, axisFormat, wide, totalName }: {
  data: Array<{ label: string; month: string; parts: [number, number, number]; muted?: boolean }>
  names: [string, string, string]     // bottom → top; colours CHART_GREY, CHART_LIGHT, CHART_BLUE
  totalName: string
  format: (v: number) => string
  axisFormat?: (v: number) => string
  wide?: boolean
}) {
  const D = wide ? WIDE : NARROW
  const { setTip, node } = useTip(D)
  const total = (d: { parts: [number, number, number] }) => d.parts[0] + d.parts[1] + d.parts[2]
  const yMax = niceMax(Math.max(1, ...data.map(total)))
  const n = data.length
  const slot = (D.W - PAD.left - PAD.right) / n
  const barW = Math.max(6, slot - 8)
  const colours = SPLIT_COLOURS
  return (
    <div>
      <div className="rowflex" style={{ gap: 12, marginBottom: 2 }}>
        {names.map((nm, i) => (
          <span key={nm} className="tiny" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <span style={{ width: 9, height: 9, borderRadius: 3, background: colours[i], display: 'inline-block' }} />{nm}
          </span>
        ))}
      </div>
      <div style={{ position: 'relative' }}>
        <Frame D={D} yMax={yMax} yFmt={axisFormat ?? format}>
          {data.map((d, i) => {
            const x = plotX(D, i, n) + (slot - barW) / 2
            const tip = (cx: number, cy: number) => setTip({ x: cx, y: cy, body: <><strong>{d.label}</strong> — {totalName}: {format(total(d))} · {names[0]}: {format(d.parts[0])} · {names[1]}: {format(d.parts[1])} · {names[2]}: {format(d.parts[2])}{d.muted ? ' · hand-entered' : ''}</> })
            let from = 0
            return (
              <g key={d.month}>
                {d.parts.map((v, j) => {
                  const above = d.parts.slice(j + 1).some((p) => p > 0)
                  const el = <Bar D={D} key={j} x={x} w={barW} v={v} from={from} flat={above} yMax={yMax} fill={colours[j]} onEnter={tip} onLeave={() => setTip(null)} />
                  from += v
                  return el
                })}
              </g>
            )
          })}
          <XLabels D={D} labels={data.map((d) => d.label)} />
        </Frame>
        {node}
      </div>
      <TableFallback head={['Month', totalName, ...names]} rows={data.map((d) => [d.label, format(total(d)), ...d.parts.map(format)])} />
    </div>
  )
}

/** Single-series trend line with gaps for empty months (2px line, 8px markers). */
export function TrendLineChart({ data, format, name }: {
  data: Array<{ label: string; month: string; v: number | null }>
  format: (v: number) => string
  name: string
}) {
  const D = NARROW
  const { setTip, node } = useTip(D)
  const yMax = 1
  const n = data.length
  const cx = (i: number) => plotX(D, i, n) + (D.W - PAD.left - PAD.right) / n / 2
  // Broken segments: a month with no decisions is a GAP, not a zero.
  const segments: Array<Array<{ i: number; v: number }>> = []
  let cur: Array<{ i: number; v: number }> = []
  data.forEach((d, i) => {
    if (d.v == null) { if (cur.length) segments.push(cur); cur = [] }
    else cur.push({ i, v: d.v })
  })
  if (cur.length) segments.push(cur)
  return (
    <div>
      <div style={{ position: 'relative' }}>
        <Frame D={D} yMax={yMax} yFmt={(v) => `${Math.round(v * 100)}%`}>
          {segments.map((seg, k) => (
            <polyline key={k} fill="none" stroke={CHART_BLUE} strokeWidth={2} strokeLinecap="round"
              points={seg.map((p) => `${cx(p.i)},${plotY(D, p.v, yMax)}`).join(' ')} />
          ))}
          {data.map((d, i) => d.v == null ? null : (
            <g key={d.month}
              onMouseEnter={() => setTip({ x: cx(i), y: plotY(D, d.v!, yMax), body: <><strong>{d.label}</strong> — {name}: {format(d.v!)}</> })}
              onMouseLeave={() => setTip(null)}>
              <circle cx={cx(i)} cy={plotY(D, d.v, yMax)} r={8} fill="transparent" />
              <circle cx={cx(i)} cy={plotY(D, d.v, yMax)} r={4} fill={CHART_BLUE} stroke="#fff" strokeWidth={2} />
            </g>
          ))}
          <XLabels D={D} labels={data.map((d) => d.label)} />
        </Frame>
        {node}
      </div>
      <TableFallback head={['Month', name]} rows={data.map((d) => [d.label, d.v == null ? '—' : format(d.v)])} />
    </div>
  )
}

function Legend({ names, colours }: { names: string[]; colours: string[] }) {
  return (
    <div className="rowflex" style={{ gap: 12, marginBottom: 2 }}>
      {names.map((n, i) => (
        <span key={n} className="tiny" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <span style={{ width: 9, height: 9, borderRadius: 3, background: colours[i], display: 'inline-block' }} />{n}
        </span>
      ))}
    </div>
  )
}

/**
 * Decision 201: three grouped bars per month — One-off · ARR · TCV. Grouped,
 * never stacked: TCV is not the sum of the other two (it is one-off + annual
 * × term years), so stacking would draw a lie. `fyMonth` (YYYY-MM) draws a
 * light divider before that month — the 1 April financial-year boundary.
 */
export function TripleBarChart({ data, names, format, axisFormat, fyMonth, wide, rate }: {
  data: Array<{ label: string; month: string; a: number; b: number; c: number }>
  names: [string, string, string]
  format: (v: number) => string
  axisFormat?: (v: number) => string
  fyMonth?: string
  wide?: boolean
  /**
   * Decision 201 (Mark): the acceptance rate on the same chart. Drawn as a
   * BAND across the top — same months, the line with a % label at each point,
   * and NO second axis: a right-hand % axis would invite reading line height
   * against bar height, which means nothing. null = no decisions that month (gap).
   */
  rate?: Array<number | null>
}) {
  const D = wide ? WIDE : NARROW
  const { setTip, node } = useTip(D)
  // With a rate band the bars keep the lower ~72% of the plot; the band takes the top.
  const bandFrac = rate ? 0.26 : 0
  const dataMax = niceMax(Math.max(1, ...data.map((d) => Math.max(d.a, d.b, d.c))))
  const yMax = dataMax / (1 - bandFrac)   // the money ticks stay at 0 · ½ · dataMax
  const n = data.length
  const slot = (D.W - PAD.left - PAD.right) / n
  const barW = Math.max(3, Math.min(wide ? 18 : 12, (slot - 8) / 3 - 1))
  const vals = (d: { a: number; b: number; c: number }) => [d.a, d.b, d.c]
  const cx = (i: number) => plotX(D, i, n) + slot / 2
  const plotH = D.H - PAD.top - PAD.bottom
  const bandTop = PAD.top + 10, bandBottom = PAD.top + plotH * bandFrac - 6
  const ry = (r: number) => bandBottom - (bandBottom - bandTop) * r
  const segments: Array<Array<{ i: number; r: number }>> = []
  if (rate) {
    let cur: Array<{ i: number; r: number }> = []
    rate.forEach((r, i) => { if (r == null) { if (cur.length) segments.push(cur); cur = [] } else cur.push({ i, r }) })
    if (cur.length) segments.push(cur)
  }
  return (
    <div>
      <div className="rowflex" style={{ gap: 12, marginBottom: 2, flexWrap: 'wrap' }}>
        <Legend names={names} colours={SPLIT_COLOURS} />
        {rate && (
          <span className="tiny" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <span style={{ width: 14, height: 0, borderTop: `2px solid ${RATE_COLOUR}`, display: 'inline-block' }} />Acceptance rate (portal quotes: accepted ÷ decided that month)
          </span>
        )}
      </div>
      <div style={{ position: 'relative' }}>
        <Frame D={D} yMax={yMax} yFmt={axisFormat ?? format} ticks={[0, dataMax / 2, dataMax]}>
          {rate && <line x1={PAD.left} x2={D.W - PAD.right} y1={bandBottom + 4} y2={bandBottom + 4} stroke="var(--line, #d9e2e8)" strokeWidth={1} />}
          {segments.map((seg, k) => (
            <polyline key={`r${k}`} fill="none" stroke={RATE_COLOUR} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"
              points={seg.map((p) => `${cx(p.i)},${ry(p.r)}`).join(' ')} />
          ))}
          {rate && rate.map((r, i) => r == null ? null : (
            <g key={`p${i}`} onMouseEnter={() => setTip({ x: cx(i), y: ry(r), body: <><strong>{data[i].label}</strong> — acceptance rate: {Math.round(r * 100)}%</> })}
              onMouseLeave={() => setTip(null)}>
              <circle cx={cx(i)} cy={ry(r)} r={7} fill="transparent" />
              <circle cx={cx(i)} cy={ry(r)} r={3} fill={RATE_COLOUR} stroke="#fff" strokeWidth={1.5} />
              <text x={cx(i)} y={ry(r) - 6} textAnchor="middle" fontSize={8.5} fill="var(--ink-soft, #66757e)">{Math.round(r * 100)}%</text>
            </g>
          ))}
          {data.map((d, i) => {
            const x0 = plotX(D, i, n) + (slot - barW * 3 - 4) / 2
            return (
              <g key={d.month}>
                {fyMonth && d.month === fyMonth && i > 0 && (
                  <line x1={plotX(D, i, n)} x2={plotX(D, i, n)} y1={PAD.top} y2={D.H - PAD.bottom}
                    stroke="var(--ink-soft, #66757e)" strokeWidth={1} strokeDasharray="3 3" opacity={0.6}>
                    <title>Financial year starts</title>
                  </line>
                )}
                {vals(d).map((v, k) => (
                  <Bar D={D} key={k} x={x0 + k * (barW + 2)} w={barW} v={v} yMax={yMax} fill={SPLIT_COLOURS[k]}
                    onEnter={(cx, cy) => setTip({ x: cx, y: cy, body: <><strong>{d.label}</strong> — {names[k]}: {format(v)}</> })}
                    onLeave={() => setTip(null)} />
                ))}
              </g>
            )
          })}
          <XLabels D={D} labels={data.map((d) => d.label)} />
        </Frame>
        {node}
      </div>
      <TableFallback head={['Month', ...names, ...(rate ? ['Acceptance rate'] : [])]}
        rows={data.map((d, i) => [d.label, format(d.a), format(d.b), format(d.c), ...(rate ? [rate[i] == null ? '—' : `${Math.round(rate[i]! * 100)}%`] : [])])} />
    </div>
  )
}

/**
 * Decision 201: the pipeline table — one row per stage; the three money
 * columns (One-off · ARR · TCV) each show the figure over a thin bar scaled
 * to ONE shared maximum, so lengths compare across columns as well as rows.
 * Rows are clickable when `onRow` is given (→ the Quotes list, stage-filtered).
 */
/**
 * Decision 215 (Mark, 2026-09-10): the cockpit's pipeline table — figures, not
 * bars. Per stage: quotes · one-off · ARR · TCV · margin % (on TCV). A totals
 * row sums every money column; the margin total is the POPULATION's
 * (Σ margin ÷ Σ TCV), never an average of the rows.
 * Decision 245 (Mark, 2026-09-19): the Weight % and Weighted columns are gone —
 * the one weighted forecast lives in the Wave9 Hub's ARR Cockpit.
 */
export function PipelineTable({ rows, onRow }: {
  rows: Array<{ key: string; label: string; n: number; a: number; b: number; c: number; buy: number; marginPct: number | null }>
  onRow?: (key: string) => void
}) {
  const gbp = (v: number) => formatGBP(Math.round(v)).replace(/\.00$/, '')
  const pct = (v: number | null) => (v == null ? '—' : `${v.toLocaleString('en-GB', { maximumFractionDigits: 1 })}%`)
  const total = rows.reduce((t, r) => ({
    n: t.n + r.n, a: t.a + r.a, b: t.b + r.b, c: t.c + r.c, buy: t.buy + r.buy,
  }), { n: 0, a: 0, b: 0, c: 0, buy: 0 })
  const totalMargin = total.c > 0 ? ((total.c - total.buy) / total.c) * 100 : null
  return (
    <div className="table-wrap">
      <table className="small" data-testid="pipeline-table">
        <thead>
          <tr>
            <th>Stage</th>
            <th className="num">Quotes</th>
            <th className="num">One-off</th>
            <th className="num">ARR</th>
            <th className="num">TCV</th>
            <th className="num" title="(TCV − cost) ÷ TCV">Margin %</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} onClick={onRow ? () => onRow(r.key) : undefined}
              title={onRow ? 'Open these quotes in the list' : undefined}
              style={{ cursor: onRow ? 'pointer' : 'default' }}>
              <td style={{ fontWeight: 600 }}>{r.label}</td>
              <td className="num">{r.n}</td>
              <td className="num">{gbp(r.a)}</td>
              <td className="num">{gbp(r.b)}</td>
              <td className="num" style={{ fontWeight: 600 }}>{gbp(r.c)}</td>
              <td className="num">{pct(r.marginPct)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr data-testid="pipeline-totals">
            <td><strong>Total</strong></td>
            <td className="num"><strong>{total.n}</strong></td>
            <td className="num"><strong>{gbp(total.a)}</strong></td>
            <td className="num"><strong>{gbp(total.b)}</strong></td>
            <td className="num"><strong>{gbp(total.c)}</strong></td>
            <td className="num"><strong>{pct(totalMargin == null ? null : Math.round(totalMargin * 10) / 10)}</strong></td>
          </tr>
        </tfoot>
      </table>
    </div>
  )
}

export { monthShort }
